import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { Trash2, Layers, Plus, X } from 'lucide-react';

interface PortfolioGroup {
  id: number;
  name?: string;
  groupName?: string;
  code?: string;
  description?: string;
  groupType?: 'STRATEGY' | 'MANDATE' | 'VEHICLE' | 'REGION' | 'CUSTOM';
  portfolioCount?: number;
  totalAum?: number;
  benchmark?: string;
  status?: 'ACTIVE' | 'INACTIVE';
  createdAt?: string;
  portfolios?: { id: number; name?: string; portfolioName?: string; aum?: number }[];
}

interface Portfolio {
  id: number;
  portfolioName: string;
  aum?: number;
}

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div<{ embedded?: boolean }>`
  padding: ${props => props.embedded ? '1.5rem' : '2rem'};
  background: ${props => props.embedded ? 'transparent' : 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'};
  min-height: ${props => props.embedded ? 'auto' : 'calc(100vh - 64px)'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    &:hover {
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: #e2e8f0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  `}
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #94a3b8;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.875rem;

  &::placeholder {
    color: #64748b;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const GroupsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const GroupCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.2s;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }
`;

const GroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const GroupInfo = styled.div``;

const GroupName = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 0.25rem 0;
`;

const GroupCode = styled.span`
  font-size: 0.75rem;
  color: #64748b;
  font-family: monospace;
`;

const TypeBadge = styled.span<{ type?: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  ${props => {
    switch (props.type) {
      case 'STRATEGY':
        return 'background: rgba(59, 130, 246, 0.2); color: #3b82f6;';
      case 'MANDATE':
        return 'background: rgba(168, 85, 247, 0.2); color: #a855f7;';
      case 'VEHICLE':
        return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
      case 'REGION':
        return 'background: rgba(234, 179, 8, 0.2); color: #eab308;';
      default:
        return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const GroupStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 1rem 0;
  padding: 1rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const GroupStat = styled.div`
  text-align: center;
`;

const GroupStatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
`;

const GroupStatLabel = styled.div`
  font-size: 0.7rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PortfolioList = styled.div`
  margin-top: 1rem;
`;

const PortfolioItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
`;

const PortfolioName = styled.span`
  font-size: 0.875rem;
  color: #e2e8f0;
`;

const PortfolioAum = styled.span`
  font-size: 0.875rem;
  color: #94a3b8;
  font-family: monospace;
`;

const CardActions = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s;

  ${GroupCard}:hover & {
    opacity: 1;
  }
`;

const CardActionButton = styled.button<{ danger?: boolean }>`
  padding: 0.5rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.danger ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  color: ${props => props.danger ? '#ef4444' : '#3b82f6'};

  &:hover {
    background: ${props => props.danger ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.2)'};
  }
`;

// Modal Styles
const ModalBackdrop = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'flex' : 'none'};
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalCard = styled.div`
  background: #0f172a;
  border-radius: 24px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0,0,0,0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: ${slideUp} 0.3s ease-out;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #e2e8f0;
  margin: 0;
`;

const ModalClose = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(15, 23, 42, 0.4);
  color: #94a3b8;
  cursor: pointer;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(15, 23, 42, 0.6);
    color: #ffffff;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  max-height: calc(80vh - 140px);
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ModalButton = styled.button<{ primary?: boolean }>`
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: ${props => props.primary ? 'none' : '1px solid rgba(148, 163, 184, 0.2)'};
  background: ${props => props.primary ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(30, 41, 59, 0.6)'};
  color: white;

  &:hover {
    transform: translateY(-1px);
    background: ${props => props.primary ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' : 'rgba(30, 41, 59, 0.8)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 16px;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  font-size: 0.9375rem;
  background: rgba(15, 23, 42, 0.6);
  color: #e2e8f0;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  font-size: 0.9375rem;
  background: rgba(30, 41, 59, 0.6);
  color: #e2e8f0;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0';
  }
  if (value >= 1000000000) {
    return `$${(value / 1000000000).toFixed(1)}B`;
  }
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

interface PortfolioGroupsScreenProps {
  embedded?: boolean;
}

const PortfolioGroupsScreen: React.FC<PortfolioGroupsScreenProps> = ({ embedded = false }) => {
  const [groups, setGroups] = useState<PortfolioGroup[]>([]);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [error, setError] = useState<string | null>(null);

  // Group Management State
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PortfolioGroup | null>(null);
  const [groupForm, setGroupForm] = useState({
    groupName: '',
    description: '',
    groupType: 'CUSTOM' as 'STRATEGY' | 'MANDATE' | 'VEHICLE' | 'REGION' | 'CUSTOM'
  });

  // Mock data for institutional portfolio groups
  const mockGroups: PortfolioGroup[] = [
    {
      id: 1,
      name: 'Investment Grade Credit',
      groupName: 'Investment Grade Credit',
      code: 'IG-CREDIT',
      description: 'Investment grade corporate bond portfolios',
      groupType: 'STRATEGY',
      portfolioCount: 8,
      totalAum: 2500000000,
      benchmark: 'Bloomberg US Corporate IG',
      status: 'ACTIVE',
      createdAt: '2022-01-15',
      portfolios: [
        { id: 101, name: 'IG Core Fund', portfolioName: 'IG Core Fund', aum: 850000000 },
        { id: 102, name: 'IG Short Duration', portfolioName: 'IG Short Duration', aum: 425000000 },
        { id: 103, name: 'IG Long Duration', portfolioName: 'IG Long Duration', aum: 625000000 },
      ],
    },
    {
      id: 2,
      name: 'High Yield',
      groupName: 'High Yield',
      code: 'HY-CREDIT',
      description: 'High yield corporate bond portfolios',
      groupType: 'STRATEGY',
      portfolioCount: 5,
      totalAum: 1800000000,
      benchmark: 'Bloomberg US Corporate HY',
      status: 'ACTIVE',
      createdAt: '2022-03-20',
      portfolios: [
        { id: 201, name: 'HY Core Fund', portfolioName: 'HY Core Fund', aum: 750000000 },
        { id: 202, name: 'HY Opportunistic', portfolioName: 'HY Opportunistic', aum: 550000000 },
      ],
    },
    {
      id: 3,
      name: 'Private Credit',
      groupName: 'Private Credit',
      code: 'PC-DIRECT',
      description: 'Direct lending and private credit portfolios',
      groupType: 'VEHICLE',
      portfolioCount: 4,
      totalAum: 3200000000,
      status: 'ACTIVE',
      createdAt: '2021-08-10',
      portfolios: [
        { id: 301, name: 'Direct Lending Fund I', portfolioName: 'Direct Lending Fund I', aum: 1200000000 },
        { id: 302, name: 'Direct Lending Fund II', portfolioName: 'Direct Lending Fund II', aum: 900000000 },
      ],
    },
    {
      id: 4,
      name: 'Insurance Mandates',
      groupName: 'Insurance Mandates',
      code: 'INS-MANDATE',
      description: 'Insurance company investment mandates',
      groupType: 'MANDATE',
      portfolioCount: 12,
      totalAum: 5500000000,
      benchmark: 'Custom Insurance Benchmark',
      status: 'ACTIVE',
      createdAt: '2020-05-01',
      portfolios: [
        { id: 401, name: 'Life Insurance Core', portfolioName: 'Life Insurance Core', aum: 1800000000 },
        { id: 402, name: 'P&C Reserve', portfolioName: 'P&C Reserve', aum: 1200000000 },
        { id: 403, name: 'Annuity Assets', portfolioName: 'Annuity Assets', aum: 950000000 },
      ],
    },
    {
      id: 5,
      name: 'EMEA Portfolios',
      groupName: 'EMEA Portfolios',
      code: 'EMEA-REG',
      description: 'Europe, Middle East, and Africa regional portfolios',
      groupType: 'REGION',
      portfolioCount: 6,
      totalAum: 1200000000,
      benchmark: 'Bloomberg Euro Aggregate',
      status: 'ACTIVE',
      createdAt: '2022-06-15',
      portfolios: [
        { id: 501, name: 'Euro IG Credit', portfolioName: 'Euro IG Credit', aum: 450000000 },
        { id: 502, name: 'Sterling Credit', portfolioName: 'Sterling Credit', aum: 320000000 },
      ],
    },
    {
      id: 6,
      name: 'CLO Portfolios',
      groupName: 'CLO Portfolios',
      code: 'CLO-VEH',
      description: 'Collateralized Loan Obligation portfolios',
      groupType: 'VEHICLE',
      portfolioCount: 3,
      totalAum: 2100000000,
      status: 'ACTIVE',
      createdAt: '2021-11-30',
      portfolios: [
        { id: 601, name: 'CLO 2021-1', portfolioName: 'CLO 2021-1', aum: 800000000 },
        { id: 602, name: 'CLO 2022-1', portfolioName: 'CLO 2022-1', aum: 750000000 },
      ],
    },
  ];

  const fetchData = useCallback(async () => {
    try {
      const [groupRes, portfolioRes] = await Promise.all([
        axios.get('/api/portfolio-groups'),
        axios.get('/api/dimensions/portfolios')
      ]);
      const groupsData = groupRes.data?.content || groupRes.data || [];
      const portfoliosData = portfolioRes.data?.content || portfolioRes.data || [];
      setGroups(Array.isArray(groupsData) && groupsData.length > 0 ? groupsData : mockGroups);
      setPortfolios(Array.isArray(portfoliosData) ? portfoliosData : []);
    } catch (error) {
      console.log('Using mock portfolio groups data');
      setGroups(mockGroups);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get display name for group (handles both name and groupName)
  const getGroupDisplayName = (group: PortfolioGroup) => {
    return group.name || group.groupName || 'Unnamed Group';
  };

  // Get portfolio display name (handles both name and portfolioName)
  const getPortfolioDisplayName = (portfolio: { id: number; name?: string; portfolioName?: string; aum?: number }) => {
    return portfolio.name || portfolio.portfolioName || 'Unnamed Portfolio';
  };

  // Portfolio Group Management Functions
  const handleCreateGroup = async () => {
    if (!groupForm.groupName.trim()) {
      setError('Group name is required');
      return;
    }
    try {
      await axios.post('/api/portfolio-groups', {
        groupName: groupForm.groupName,
        description: groupForm.description || '',
        groupType: groupForm.groupType
      });
      await fetchData();
      setGroupForm({ groupName: '', description: '', groupType: 'CUSTOM' });
      setEditingGroup(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create group');
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !groupForm.groupName.trim()) {
      setError('Group name is required');
      return;
    }
    try {
      await axios.put(`/api/portfolio-groups/${editingGroup.id}`, {
        groupName: groupForm.groupName,
        description: groupForm.description || '',
        groupType: groupForm.groupType
      });
      await fetchData();
      setGroupForm({ groupName: '', description: '', groupType: 'CUSTOM' });
      setEditingGroup(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update group');
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;
    try {
      await axios.delete(`/api/portfolio-groups/${groupId}`);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete group');
    }
  };

  const handleAddPortfoliosToGroup = async (groupId: number, portfolioIds: number[]) => {
    try {
      await axios.post(`/api/portfolio-groups/${groupId}/portfolios`, { portfolioIds });
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add portfolios');
    }
  };

  const handleRemovePortfolioFromGroup = async (groupId: number, portfolioId: number) => {
    try {
      await axios.delete(`/api/portfolio-groups/${groupId}/portfolios/${portfolioId}`);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove portfolio');
    }
  };

  const startEditGroup = (group: PortfolioGroup) => {
    setEditingGroup(group);
    setGroupForm({
      groupName: getGroupDisplayName(group),
      description: group.description || '',
      groupType: group.groupType || 'CUSTOM'
    });
    setShowGroupManager(true);
  };

  const filteredGroups = groups.filter(g => {
    const searchLower = searchTerm.toLowerCase();
    const groupName = getGroupDisplayName(g).toLowerCase();
    const groupCode = (g.code?.toLowerCase() || '');
    const matchesSearch = groupName.includes(searchLower) || groupCode.includes(searchLower);
    const matchesType = typeFilter === 'ALL' || g.groupType === typeFilter;
    return matchesSearch && matchesType;
  });

  const totalAum = groups.reduce((sum, g) => sum + (g.totalAum || 0), 0);
  const totalPortfolios = groups.reduce((sum, g) => sum + (g.portfolioCount || 0), 0);

  return (
    <Container embedded={embedded}>
      {!embedded && (
        <Header>
          <Title>Portfolio Groups</Title>
          <HeaderActions>
            <Button variant="secondary">Import</Button>
            <Button variant="primary" onClick={() => { setEditingGroup(null); setGroupForm({ groupName: '', description: '', groupType: 'CUSTOM' }); setShowGroupManager(true); }}>
              <Plus className="w-4 h-4" /> New Group
            </Button>
          </HeaderActions>
        </Header>
      )}

      {embedded && (
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">Portfolio Groups</h2>
            <p className="text-sm text-slate-400">Manage portfolio groupings for institutional trading</p>
          </div>
          <Button variant="primary" onClick={() => { setEditingGroup(null); setGroupForm({ groupName: '', description: '', groupType: 'CUSTOM' }); setShowGroupManager(true); }}>
            <Plus className="w-4 h-4" /> New Group
          </Button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <StatsGrid>
        <StatCard>
          <StatValue>{groups.length}</StatValue>
          <StatLabel>Total Groups</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{formatCurrency(totalAum)}</StatValue>
          <StatLabel>Total AUM</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{totalPortfolios}</StatValue>
          <StatLabel>Total Portfolios</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{groups.filter(g => g.status === 'ACTIVE').length}</StatValue>
          <StatLabel>Active Groups</StatLabel>
        </StatCard>
      </StatsGrid>

      <SearchBar>
        <SearchInput
          type="text"
          placeholder="Search groups..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="ALL">All Types</option>
          <option value="STRATEGY">Strategy</option>
          <option value="MANDATE">Mandate</option>
          <option value="VEHICLE">Vehicle</option>
          <option value="REGION">Region</option>
          <option value="CUSTOM">Custom</option>
        </Select>
      </SearchBar>

      <GroupsGrid>
        {filteredGroups.map((group) => (
          <GroupCard key={group.id}>
            <CardActions>
              <CardActionButton onClick={() => startEditGroup(group)}>
                <Layers className="w-4 h-4" />
              </CardActionButton>
              <CardActionButton danger onClick={() => handleDeleteGroup(group.id)}>
                <Trash2 className="w-4 h-4" />
              </CardActionButton>
            </CardActions>

            <GroupHeader>
              <GroupInfo>
                <GroupName>{getGroupDisplayName(group)}</GroupName>
                {group.code && <GroupCode>{group.code}</GroupCode>}
              </GroupInfo>
              <TypeBadge type={group.groupType}>{group.groupType || 'CUSTOM'}</TypeBadge>
            </GroupHeader>

            <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: '0.5rem 0' }}>
              {group.description || 'No description'}
            </p>

            <GroupStats>
              <GroupStat>
                <GroupStatValue>{formatCurrency(group.totalAum)}</GroupStatValue>
                <GroupStatLabel>Total AUM</GroupStatLabel>
              </GroupStat>
              <GroupStat>
                <GroupStatValue>{group.portfolioCount || group.portfolios?.length || 0}</GroupStatValue>
                <GroupStatLabel>Portfolios</GroupStatLabel>
              </GroupStat>
            </GroupStats>

            {group.benchmark && (
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem' }}>
                Benchmark: <span style={{ color: '#94a3b8' }}>{group.benchmark}</span>
              </div>
            )}

            {group.portfolios && group.portfolios.length > 0 && (
              <PortfolioList>
                <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                  Top Portfolios
                </div>
                {group.portfolios.slice(0, 3).map((portfolio) => (
                  <PortfolioItem key={portfolio.id}>
                    <PortfolioName>{getPortfolioDisplayName(portfolio)}</PortfolioName>
                    <PortfolioAum>{formatCurrency(portfolio.aum)}</PortfolioAum>
                  </PortfolioItem>
                ))}
                {group.portfolios.length > 3 && (
                  <div style={{ fontSize: '0.75rem', color: '#3b82f6', marginTop: '0.5rem' }}>
                    +{group.portfolios.length - 3} more portfolios
                  </div>
                )}
              </PortfolioList>
            )}
          </GroupCard>
        ))}
      </GroupsGrid>

      {/* Group Manager Modal */}
      <ModalBackdrop show={showGroupManager}>
        <ModalCard>
          <ModalHeader>
            <ModalTitle>Manage Portfolio Groups</ModalTitle>
            <ModalClose onClick={() => { setShowGroupManager(false); setEditingGroup(null); setGroupForm({ groupName: '', description: '', groupType: 'CUSTOM' }); }}>
              <X className="w-5 h-5" />
            </ModalClose>
          </ModalHeader>
          <ModalBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Form Side */}
              <div className="space-y-4 p-4 rounded-xl bg-white/5 border border-white/10">
                <h4 className="text-sm font-bold text-blue-400 uppercase tracking-wider">
                  {editingGroup ? 'Edit Group' : 'Create New Group'}
                </h4>
                <InputGroup>
                  <InputLabel>Group Name</InputLabel>
                  <StyledInput
                    value={groupForm.groupName}
                    onChange={e => setGroupForm({ ...groupForm, groupName: e.target.value })}
                    placeholder="e.g., Core Fixed Income"
                  />
                </InputGroup>
                <InputGroup>
                  <InputLabel>Description</InputLabel>
                  <StyledInput
                    value={groupForm.description}
                    onChange={e => setGroupForm({ ...groupForm, description: e.target.value })}
                    placeholder="Optional description..."
                  />
                </InputGroup>
                <InputGroup>
                  <InputLabel>Group Type</InputLabel>
                  <StyledSelect
                    value={groupForm.groupType}
                    onChange={e => setGroupForm({ ...groupForm, groupType: e.target.value as any })}
                  >
                    <option value="STRATEGY">Strategy</option>
                    <option value="MANDATE">Mandate</option>
                    <option value="VEHICLE">Vehicle</option>
                    <option value="REGION">Region</option>
                    <option value="CUSTOM">Custom</option>
                  </StyledSelect>
                </InputGroup>
                <div className="flex gap-2">
                  <ModalButton primary onClick={editingGroup ? handleUpdateGroup : handleCreateGroup} disabled={!groupForm.groupName}>
                    {editingGroup ? 'Save Changes' : 'Create Group'}
                  </ModalButton>
                  {editingGroup && (
                    <ModalButton onClick={() => { setEditingGroup(null); setGroupForm({ groupName: '', description: '', groupType: 'CUSTOM' }); }}>
                      Cancel
                    </ModalButton>
                  )}
                </div>
              </div>

              {/* List Side */}
              <div className="space-y-3">
                <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider px-1">Existing Groups</h4>
                <div className="max-h-96 overflow-y-auto space-y-2 pr-2">
                  {groups.map(g => (
                    <div key={g.id} className="p-3 rounded-xl bg-slate-900/50 border border-white/5 group hover:border-blue-500/30 transition-all">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-bold text-white">{getGroupDisplayName(g)}</div>
                          <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">
                            {g.portfolios?.length || g.portfolioCount || 0} Portfolios
                          </div>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => {
                              setEditingGroup(g);
                              setGroupForm({
                                groupName: getGroupDisplayName(g),
                                description: g.description || '',
                                groupType: g.groupType || 'CUSTOM'
                              });
                            }}
                            className="p-1.5 rounded-lg hover:bg-blue-500/20 text-blue-400"
                          >
                            <Layers className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteGroup(g.id)}
                            className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      {/* Portfolios List */}
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {g.portfolios?.map((p) => (
                          <span key={p.id} className="px-2 py-0.5 rounded bg-white/5 text-[9px] text-slate-400 border border-white/5 flex items-center gap-1">
                            {getPortfolioDisplayName(p)}
                            <button
                              onClick={() => handleRemovePortfolioFromGroup(g.id, p.id)}
                              className="hover:text-red-400"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        {portfolios.length > 0 && (
                          <StyledSelect
                            value=""
                            onChange={e => e.target.value && handleAddPortfoliosToGroup(g.id, [parseInt(e.target.value)])}
                            className="!w-auto !py-0.5 !px-2 !text-[9px] !h-auto !bg-blue-500/10 !border-blue-500/20 !text-blue-400"
                            style={{ width: 'auto', padding: '2px 8px', fontSize: '9px', height: 'auto' }}
                          >
                            <option value="">+ Add</option>
                            {portfolios.filter(p => !g.portfolios?.some((gp) => gp.id === p.id)).map(p => (
                              <option key={p.id} value={p.id}>{p.portfolioName}</option>
                            ))}
                          </StyledSelect>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <ModalButton primary onClick={() => setShowGroupManager(false)}>Done</ModalButton>
          </ModalFooter>
        </ModalCard>
      </ModalBackdrop>
    </Container>
  );
};

export default PortfolioGroupsScreen;
